/**
 * @tyndo/ui-kit/domain/EvidenceTimeline
 *
 * Closes G2.3 from docs/latest-analysis.md.
 *
 * Renders the audit trail next to a mutation button, with each entry
 * timestamped and actor-stamped. The killer feature is the optional
 * `predictedEffect` prop — a one-line preview of the downstream
 * change a mutation would cause, surfaced BEFORE the user clicks
 * commit. This is what makes "evidence-first commands" feel real.
 *
 * Wire pattern at the call site:
 *
 * ```tsx
 * <EvidenceTimeline
 *   recordType="WorkOrder"
 *   recordId={wo.id}
 *   entries={auditTrail}
 *   predictedEffect={
 *     <PredictedEffect>
 *       Releasing this WO will consume {bom.qty} of {bom.material} from
 *       Lot {lot.number}, decrement Inventory.OnHand by {bom.qty}, and
 *       post a $42.00 valuation entry.
 *     </PredictedEffect>
 *   }
 * />
 * ```
 */

import * as React from 'react';
import { History, User, Eye, Sparkles } from 'lucide-react';
import { ScrollArea } from '../primitives/scroll-area';
import { Badge } from '../primitives/badge';

export interface EvidenceTimelineEntry {
  id: string;
  /** Wall-clock timestamp of the event. */
  at: string | Date;
  /** Human-readable summary ("Released", "Held for QA review"). */
  summary: string;
  /** Operator id / actor name. */
  by?: string;
  /** Tone/severity for the row dot color. */
  tone?: 'info' | 'success' | 'warning' | 'critical';
  /** Optional payload preview (collapsed by default; click to expand). */
  payload?: string;
}

export interface EvidenceTimelineProps {
  /** Record type label ("WorkOrder", "NCR", "PO"). */
  recordType: string;
  /** Record id used in data attributes for analytics. */
  recordId: string;
  /** Most-recent-first entry list. */
  entries: EvidenceTimelineEntry[];
  /** Optional preview of the downstream effect a pending command would produce. */
  predictedEffect?: React.ReactNode;
  /** Optional max-height for the scroll area; defaults to 320px. */
  maxHeight?: number;
  className?: string;
}

const toneColor = (tone?: EvidenceTimelineEntry['tone']) => {
  switch (tone) {
    case 'success':
      return 'bg-state-running';
    case 'warning':
      return 'bg-alarm-minor';
    case 'critical':
      return 'bg-alarm-critical';
    case 'info':
    default:
      return 'bg-primary';
  }
};

export const EvidenceTimeline: React.FC<EvidenceTimelineProps> = ({
  recordType,
  recordId,
  entries,
  predictedEffect,
  maxHeight = 320,
  className,
}) => {
  return (
    <div className={className} data-record-type={recordType} data-record-id={recordId}>
      {predictedEffect && (
        <div className="mb-3 rounded border border-primary/40 bg-primary/5 p-3">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-primary">
            <Sparkles size={12} />
            Predicted effect
          </div>
          <div className="mt-1 text-sm text-foreground">{predictedEffect}</div>
        </div>
      )}

      <div className="flex items-center gap-1.5 border-b border-border pb-2 mb-2 text-xs font-semibold text-muted-foreground">
        <History size={12} />
        Evidence timeline
        <Badge variant="info" className="ml-auto">
          {entries.length}
        </Badge>
      </div>

      {entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">No audit entries recorded yet.</p>
      ) : (
        <ScrollArea style={{ maxHeight }}>
          <ol className="space-y-2 pl-2">
            {entries.map(entry => (
              <li key={entry.id} className="flex gap-2">
                <span
                  aria-hidden
                  className={`mt-1 inline-block h-2 w-2 flex-shrink-0 rounded-full ${toneColor(entry.tone)}`}
                />
                <div className="flex-1 text-sm">
                  <div className="font-medium text-foreground">{entry.summary}</div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <time dateTime={typeof entry.at === 'string' ? entry.at : entry.at.toISOString()}>
                      {new Date(entry.at).toLocaleString()}
                    </time>
                    {entry.by && (
                      <>
                        <span aria-hidden>·</span>
                        <span className="flex items-center gap-1">
                          <User size={10} />
                          {entry.by}
                        </span>
                      </>
                    )}
                    {entry.payload && (
                      <>
                        <span aria-hidden>·</span>
                        <details className="inline">
                          <summary className="cursor-pointer underline-offset-2 hover:underline">
                            <Eye size={10} className="inline" /> payload
                          </summary>
                          <pre className="mt-1 max-w-md overflow-auto rounded bg-surface-sunken p-1 text-xs">
                            {entry.payload}
                          </pre>
                        </details>
                      </>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </ScrollArea>
      )}
    </div>
  );
};
